import { Card } from '@/shared/components/ui/Card';
import { Button } from '@/shared/components/ui/Button';
import { Input } from '@/shared/components/ui/Input';
import { PlusCircle } from 'lucide-react';
import type { OrderState } from '../../pages/CreateSalesOrderPage';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/shared/api/axios';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from '@/routes/paths';

interface Props {
  state: OrderState;
  updateState: (updater: (prev: OrderState) => OrderState) => void;
}

interface ClientContact {
  id: string;
  name: string;
  designation: string;
  phone: string;
  email: string;
  isPrimaryContact: boolean;
}

export function ClientSelection({ state, updateState }: Props) {
  const navigate = useNavigate();

  const { data: clientsResponse, isLoading } = useQuery({
    queryKey: ['clients-prospects-all'],
    queryFn: async () => {
      const res = await api.get('/api/sales-ops/clients-prospects', { params: { limit: 100 } });
      return res.data;
    }
  });

  const clients = clientsResponse?.items || [];

  // Gap-audit follow-up: real named contacts for the selected client instead
  // of a hardcoded placeholder option — only resolvable for a client that's a
  // real AccountsCustomer with a linked Contact, see the route's own comment.
  const clientId = state.deliveredTo.clientId;
  const { data: contactsResponse } = useQuery({
    queryKey: ['sales-order-client-contacts', clientId],
    queryFn: async () => (await api.get(`/api/sales-ops/clients-prospects/${clientId}/contacts`)).data,
    enabled: !!clientId,
  });
  const contacts: ClientContact[] = contactsResponse?.contacts || [];

  return (
    <Card className="h-full border-border bg-card p-4 shadow-xs">
      <div className="flex justify-between items-center mb-3">
        <h3 className="flex items-center gap-2 text-label-sm uppercase tracking-wider text-on-surface-variant">
          Delivered To <span className="text-body-xs font-normal normal-case tracking-normal text-on-surface-subtle">(Client&apos;s details)</span>
        </h3>
      </div>
      
      <div className="space-y-4">
        <select 
          className="block h-10 w-full rounded-lg border border-input bg-surface px-3 text-data-md text-on-surface focus:border-ring focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          value={state.deliveredTo.clientId || ''}
          onChange={(e) => updateState(s => ({ ...s, deliveredTo: { ...s.deliveredTo, clientId: e.target.value } }))}
        >
          <option value="">Select a Client</option>
          {isLoading ? (
            <option disabled>Loading clients...</option>
          ) : (
            clients.map((c: any) => (
              <option key={c.id} value={c.id}>{c.name} ({c.type})</option>
            ))
          )}
        </select>

        {!state.deliveredTo.clientId && (
          <div className="flex flex-col items-center justify-center rounded-card-sm border border-border bg-muted/50 p-8 text-center">
            <p className="mb-2 text-body-sm text-on-surface-variant">Select Client/Business from the list</p>
            <span className="mb-4 text-data-xs font-semibold uppercase tracking-wider text-on-surface-subtle">OR</span>
            <Button
              onClick={() => navigate(ROUTES.svcClientsProspects)}
              className="gap-2"
            >
              <PlusCircle className="w-4 h-4" /> Add New Client
            </Button>
          </div>
        )}

        {state.deliveredTo.clientId && (
           <div className="rounded-card-sm border border-border bg-muted/40 p-4">
              <select
                className="block h-10 w-full rounded-lg border border-input bg-surface px-3 text-data-md text-on-surface focus:border-ring focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                value={state.deliveredTo.contactId || ''}
                onChange={(e) => {
                  const picked = contacts.find((c) => c.id === e.target.value);
                  updateState(s => ({ ...s, deliveredTo: { ...s.deliveredTo, contactId: e.target.value, contactName: picked?.name || '' } }));
                }}
              >
                <option value="">{contacts.length === 0 ? 'No linked contacts for this client' : 'Select Contact to Link'}</option>
                {contacts.map((c) => (
                  <option key={c.id} value={c.id}>{c.name}{c.designation ? ` — ${c.designation}` : ''}{c.isPrimaryContact ? ' (Primary)' : ''}</option>
                ))}
              </select>
           </div>
        )}

        {/* Gap-audit follow-up: mandatory Sales Order fields the spec names
            at order-creation time (customer code, credit limit, billing
            address, required dispatch/delivery dates, shipping destination,
            special instructions) — previously only capturable one step
            later, at the Dispatch hand-off. */}
        <div className="mt-4 space-y-3 rounded-card-sm border border-border bg-card p-4 shadow-xs">
          <h4 className="mb-1 border-b border-border pb-2 text-label-sm uppercase tracking-wider text-on-surface-variant">Order details</h4>
          <div className="grid grid-cols-2 gap-3">
            <Input label="Customer code" placeholder="Customer Code" value={state.customerCode} onChange={(e) => updateState(s => ({ ...s, customerCode: e.target.value }))} />
            <Input label="Credit limit" placeholder="Credit Limit (₹)" type="number" min="0" value={state.creditLimit} onChange={(e) => updateState(s => ({ ...s, creditLimit: e.target.value }))} className="font-mono tabular-nums" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Input label="Required dispatch date" type="date" value={state.requiredDispatchDate} onChange={(e) => updateState(s => ({ ...s, requiredDispatchDate: e.target.value }))} />
            <Input label="Required delivery date" type="date" value={state.requiredDeliveryDate} onChange={(e) => updateState(s => ({ ...s, requiredDeliveryDate: e.target.value }))} />
          </div>
          <Input label="Shipping destination" placeholder="e.g. Mumbai Port, Customer warehouse" value={state.shippingDestination} onChange={(e) => updateState(s => ({ ...s, shippingDestination: e.target.value }))} />
          <div className="grid grid-cols-2 gap-3">
            <Input label="Billing street" placeholder="Billing Street" value={state.billingAddress.street} onChange={(e) => updateState(s => ({ ...s, billingAddress: { ...s.billingAddress, street: e.target.value } }))} />
            <Input label="Billing city" placeholder="Billing City" value={state.billingAddress.city} onChange={(e) => updateState(s => ({ ...s, billingAddress: { ...s.billingAddress, city: e.target.value } }))} />
            <Input label="Billing state" placeholder="Billing State" value={state.billingAddress.state} onChange={(e) => updateState(s => ({ ...s, billingAddress: { ...s.billingAddress, state: e.target.value } }))} />
            <Input label="Billing PIN" placeholder="Billing PIN" value={state.billingAddress.zip} onChange={(e) => updateState(s => ({ ...s, billingAddress: { ...s.billingAddress, zip: e.target.value } }))} />
          </div>
          <div>
            <label htmlFor="special-instructions" className="mb-1 block text-body-sm font-medium text-on-surface">Special instructions</label>
            <textarea
              id="special-instructions"
              rows={2}
              className="block w-full rounded-lg border border-input bg-surface px-3 py-2 text-data-md text-on-surface focus:border-ring focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              value={state.specialInstructions}
              onChange={(e) => updateState(s => ({ ...s, specialInstructions: e.target.value }))}
            />
          </div>
        </div>

        <div className="mt-4 flex items-center gap-2 border-t border-border pt-4">
          <input 
            type="checkbox" 
            id="add-shipping" 
            className="h-4 w-4 rounded border-input text-primary focus-visible:ring-ring"
            checked={state.shipping.enabled}
            onChange={(e) => updateState(s => ({ ...s, shipping: { ...s.shipping, enabled: e.target.checked } }))}
          />
          <label htmlFor="add-shipping" className="text-body-sm font-medium text-on-surface">Add shipping details</label>
        </div>

        {state.shipping.enabled && (
          <div className="mt-4 space-y-6 rounded-card-sm border border-border bg-card p-4 shadow-xs">
            
            {/* Shipped From */}
            <div>
              <h4 className="mb-3 border-b border-border pb-2 text-label-sm uppercase tracking-wider text-on-surface-variant">Shipped from</h4>
              <select 
                className="mb-3 block h-10 w-full rounded-lg border border-input bg-surface px-3 text-data-md text-on-surface focus:border-ring focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                value={state.shipping.from.warehouseId}
                onChange={(e) => updateState(s => ({ ...s, shipping: { ...s.shipping, from: { ...s.shipping.from, warehouseId: e.target.value } } }))}
              >
                <option value="">Select Warehouse</option>
                <option value="w1">Main Warehouse</option>
              </select>
              
              <div className="flex items-center gap-2 mb-3">
                <input 
                  type="checkbox" 
                  id="same-business-address" 
                  className="h-4 w-4 rounded border-input text-primary focus-visible:ring-ring"
                  checked={state.shipping.from.sameAsBusiness}
                  onChange={(e) => updateState(s => ({ ...s, shipping: { ...s.shipping, from: { ...s.shipping.from, sameAsBusiness: e.target.checked } } }))}
                />
                <label htmlFor="same-business-address" className="text-body-sm text-on-surface-variant">Same as your business address</label>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <Input placeholder="Your Business Name" className="col-span-2" value={state.shipping.from.name} onChange={(e) => updateState(s => ({ ...s, shipping: { ...s.shipping, from: { ...s.shipping.from, name: e.target.value } } }))} />
                <select className="col-span-2 h-10 rounded-lg border border-input bg-surface px-3 text-data-md text-on-surface focus:border-ring focus:outline-none focus-visible:ring-2 focus-visible:ring-ring" value={state.shipping.from.country} onChange={(e) => updateState(s => ({ ...s, shipping: { ...s.shipping, from: { ...s.shipping.from, country: e.target.value } } }))}>
                  <option value="">Select Country</option>
                  <option value="IN">India</option>
                </select>
                <Input placeholder="Address (optional)" className="col-span-2" value={state.shipping.from.address} onChange={(e) => updateState(s => ({ ...s, shipping: { ...s.shipping, from: { ...s.shipping.from, address: e.target.value } } }))} />
                <Input placeholder="City (optional)" value={state.shipping.from.city} onChange={(e) => updateState(s => ({ ...s, shipping: { ...s.shipping, from: { ...s.shipping.from, city: e.target.value } } }))} />
                <Input placeholder="Postal Code / ZIP Code" value={state.shipping.from.zip} onChange={(e) => updateState(s => ({ ...s, shipping: { ...s.shipping, from: { ...s.shipping.from, zip: e.target.value } } }))} />
                <Input placeholder="State (optional)" className="col-span-2" value={state.shipping.from.state} onChange={(e) => updateState(s => ({ ...s, shipping: { ...s.shipping, from: { ...s.shipping.from, state: e.target.value } } }))} />
              </div>
              <button className="mt-3 text-body-sm font-medium text-primary hover:underline">Add More Fields</button>
            </div>

            {/* Shipped To */}
            <div>
              <h4 className="mb-3 border-b border-border pb-2 text-label-sm uppercase tracking-wider text-on-surface-variant">Shipped to</h4>
              <select 
                className="mb-3 block h-10 w-full rounded-lg border border-input bg-surface px-3 text-data-md text-on-surface focus:border-ring focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                value={state.shipping.to.addressId}
                onChange={(e) => updateState(s => ({ ...s, shipping: { ...s.shipping, to: { ...s.shipping.to, addressId: e.target.value } } }))}
              >
                <option value="">Select a Shipping Address</option>
                <option value="a1">Default Client Address</option>
              </select>
              
              <div className="flex items-center gap-2 mb-3">
                <input 
                  type="checkbox" 
                  id="same-client-address" 
                  className="h-4 w-4 rounded border-input text-primary focus-visible:ring-ring"
                  checked={state.shipping.to.sameAsClient}
                  onChange={(e) => updateState(s => ({ ...s, shipping: { ...s.shipping, to: { ...s.shipping.to, sameAsClient: e.target.checked } } }))}
                />
                <label htmlFor="same-client-address" className="text-body-sm text-on-surface-variant">Same as client's address</label>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <Input placeholder="Client's business name" className="col-span-2" value={state.shipping.to.name} onChange={(e) => updateState(s => ({ ...s, shipping: { ...s.shipping, to: { ...s.shipping.to, name: e.target.value } } }))} />
                <select className="col-span-2 h-10 rounded-lg border border-input bg-surface px-3 text-data-md text-on-surface focus:border-ring focus:outline-none focus-visible:ring-2 focus-visible:ring-ring" value={state.shipping.to.country} onChange={(e) => updateState(s => ({ ...s, shipping: { ...s.shipping, to: { ...s.shipping.to, country: e.target.value } } }))}>
                  <option value="">Select Country</option>
                  <option value="IN">India</option>
                </select>
                <Input placeholder="Address (optional)" className="col-span-2" value={state.shipping.to.address} onChange={(e) => updateState(s => ({ ...s, shipping: { ...s.shipping, to: { ...s.shipping.to, address: e.target.value } } }))} />
                <Input placeholder="City (optional)" value={state.shipping.to.city} onChange={(e) => updateState(s => ({ ...s, shipping: { ...s.shipping, to: { ...s.shipping.to, city: e.target.value } } }))} />
                <Input placeholder="Postal Code / ZIP Code" value={state.shipping.to.zip} onChange={(e) => updateState(s => ({ ...s, shipping: { ...s.shipping, to: { ...s.shipping.to, zip: e.target.value } } }))} />
                <Input placeholder="State (optional)" className="col-span-2" value={state.shipping.to.state} onChange={(e) => updateState(s => ({ ...s, shipping: { ...s.shipping, to: { ...s.shipping.to, state: e.target.value } } }))} />
              </div>
              
              <div className="flex items-center gap-2 mt-4">
                <input 
                  type="checkbox" 
                  id="save-to-client" 
                  className="h-4 w-4 rounded border-input text-primary focus-visible:ring-ring"
                  checked={state.shipping.to.saveToClient}
                  onChange={(e) => updateState(s => ({ ...s, shipping: { ...s.shipping, to: { ...s.shipping.to, saveToClient: e.target.checked } } }))}
                />
                <label htmlFor="save-to-client" className="text-body-sm font-medium text-on-surface">Save to client details</label>
              </div>
              <button className="mt-3 text-body-sm font-medium text-primary hover:underline">Add More Fields</button>
            </div>
          </div>
        )}
      </div>
    </Card>
  );
}
